import { Link } from "react-router-dom";
import {
  Box,
  Grid,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import EmailIcon from "@mui/icons-material/Email";
import GitHubIcon from "@mui/icons-material/GitHub";
import {
  CONTACT_EMAIL,
  DATA_REPO_LINK,
  GITHUB_ORG_LINK,
  MAX_WIDTH,
} from "../constants";
import { footerResearchLinks } from "../data/research";
import Logo from "../icons/Logo";

const dataProvenanceInitiative = [
  { name: "About", link: "/about" },
  { name: "Team", link: "/contributors" },
  { name: "Press", link: "/press" },
  { name: "FAQ", link: "/faq" },
];

const socialMediaLinks = [
  {
    label: "GitHub",
    icon: <GitHubIcon />,
    link: GITHUB_ORG_LINK,
    external: true,
  },
  { label: "Email", icon: <EmailIcon />, link: `mailto:${CONTACT_EMAIL}` },
];

const Footer = () => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: `${MAX_WIDTH}px`,
        overflow: "auto",
        padding: {
          lg: "100px 70px 50px",
          md: "80px 40px 50px",
          sm: "70px 40px 50px",
          xs: "50px 30px 50px",
        },
        mx: "auto",
        color: theme.customColors.primary.aliceBlue,
      }}
    >
      <Grid container spacing={3}>
        <Grid item lg={4} md={5} sm={6} xs={12}>
          <Box>
            <Typography
              variant="h5"
              sx={{
                color: theme.customColors.supplementary.bluish,
                fontSize: { xs: "16px" },
                fontWeight: 400,
                marginBottom: "6px",
              }}
            >
              Research and Data
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {footerResearchLinks.map((link) =>
                link.comingSoon ? (
                  <Typography
                    key={link.label}
                    sx={{
                      alignItems: "center",
                      color: theme.customColors.primary.aliceBlue,
                      display: "flex",
                      fontSize: 20,
                      gap: "8px",
                      margin: "5px 0",
                    }}
                  >
                    {link.label}
                    <Box
                      component="span"
                      sx={{
                        color: theme.customColors.supplementary.bluish,
                        fontSize: "13px",
                        fontWeight: 700,
                      }}
                    >
                      Coming soon
                    </Box>
                  </Typography>
                ) : (
                  <Link
                    key={link.label}
                    to={link.href}
                    target={link.external ? "_blank" : undefined}
                    rel={link.external ? "noreferrer" : undefined}
                    style={{
                      fontSize: 20,
                      color: theme.customColors.primary.aliceBlue,
                      display: "flex",
                      alignItems: "center",
                      margin: "5px 0",
                      gap: "5px",
                    }}
                  >
                    {link.label}
                    {link.external ? (
                      <ArrowOutwardIcon sx={{ fontSize: "18px" }} />
                    ) : null}
                  </Link>
                ),
              )}
            </Box>
          </Box>
        </Grid>
        <Grid item lg={3} md={4} sm={6} xs={12}>
          <Box>
            <Typography
              variant="h5"
              sx={{
                color: theme.customColors.supplementary.bluish,
                fontSize: { xs: "16px" },
                fontWeight: 400,
                marginBottom: "6px",
              }}
            >
              Initiative
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {dataProvenanceInitiative.map((link) => (
                <Link
                  key={link.name}
                  to={link.link}
                  style={{
                    fontSize: 20,
                    color: theme.customColors.primary.aliceBlue,
                    margin: "5px 0",
                  }}
                >
                  {link.name}
                </Link>
              ))}
            </Box>
          </Box>
        </Grid>
        <Grid item lg={5} md={12} xs={12}>
          <Box sx={{ maxWidth: "520px", marginLeft: { lg: "auto", xs: 0 } }}>
            <Typography
              variant="h5"
              sx={{
                color: theme.customColors.supplementary.bluish,
                fontSize: { xs: "16px" },
                fontWeight: 400,
                marginBottom: "10px",
              }}
            >
              Collaborate
            </Typography>
            <Typography
              sx={{
                color: theme.customColors.primary.aliceBlue,
                fontSize: { md: "24px", xs: "20px" },
                lineHeight: 1.25,
                marginBottom: "18px",
              }}
            >
              Use our data, cite our work, or collaborate with us on public
              measurement infrastructure for AI.
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "14px" }}>
              <Link
                to={DATA_REPO_LINK}
                target="_blank"
                rel="noreferrer"
                style={{
                  alignItems: "center",
                  color: theme.customColors.primary.aliceBlue,
                  display: "flex",
                  fontSize: 18,
                  fontWeight: 700,
                  gap: "5px",
                }}
              >
                Use the data
                <ArrowOutwardIcon sx={{ fontSize: "18px" }} />
              </Link>
              <Link
                to="/publications"
                style={{
                  color: theme.customColors.primary.aliceBlue,
                  fontSize: 18,
                  fontWeight: 700,
                }}
              >
                Cite the work
              </Link>
              <Link
                to={`mailto:${CONTACT_EMAIL}`}
                style={{
                  color: theme.customColors.primary.aliceBlue,
                  fontSize: 18,
                  fontWeight: 700,
                }}
              >
                Contact us
              </Link>
            </Box>
          </Box>
        </Grid>
        <Grid item md={12} xs={12} sx={{ paddingTop: "0px !important" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { sm: "row", xs: "column-reverse" },
              gap: "20px",
              justifyContent: "space-between",
              marginTop: { md: "80px", sm: "60px", xs: "50px" },
            }}
          >
            <Typography variant="h5" sx={{ fontSize: { xs: "18px" } }}>
              Data Provenance Initiative &copy; {new Date().getFullYear()}
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: { xs: "10px", lg: "15px" },
                alignItems: "center",
                "*": {
                  fontSize: { xs: "30px", sm: "32px", md: "34px" },
                },
                ".icon-link": {
                  display: "grid",
                  placeItems: "center",
                },
              }}
            >
              {socialMediaLinks.map((link) => (
                <Link
                  key={link.label}
                  aria-label={link.label}
                  className="icon-link"
                  to={link.link}
                  target={link.external ? "_blank" : undefined}
                  rel={link.external ? "noreferrer" : undefined}
                  style={{
                    color: theme.customColors.primary.aliceBlue,
                    fill: theme.customColors.primary.aliceBlue,
                    textDecoration: "none",
                  }}
                >
                  {link.icon}
                </Link>
              ))}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export function ExplorerFooter() {
  return (
    <Stack
      direction={"row"}
      gap={{ xs: 1, sm: 3 }}
      sx={{
        maxWidth: `${MAX_WIDTH}px`,
        px: { xs: "0.5rem", sm: "25px", md: "52px" },
        mx: "auto",
        py: { xs: "28px", md: "32px" },
        alignItems: "center",
      }}
    >
      <IconButton
        LinkComponent={Link}
        {...({ to: "/" } as any)}
        sx={() => ({
          padding: 0,
          "&>*": {
            width: { xs: "50px", md: "50px" },
            height: { xs: "50px", md: "50px" },
            fill: "black",
          },
        })}
      >
        <Logo />
      </IconButton>
      <Typography sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem", md: "2rem" } }}>
        Data Provenance Explorer
      </Typography>
    </Stack>
  );
}

export default Footer;
